import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { 
  ArrowLeft, 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  Wallet,
  CreditCard,
  PiggyBank,
  Receipt,
  Download,
  Calendar,
  Plus,
  Trash2,
  Edit2,
  AlertCircle,
  CheckCircle2,
  Clock,
  Banknote,
  FileText,
  Scale,
  ArrowUpDown,
  Building2,
  Coins,
  CircleDollarSign,
  Users,
  UserPlus,
  BadgeCheck,
  Calculator,
  Briefcase,
  Printer
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  });
};

// Expense categories
const EXPENSE_CATEGORIES = [
  'Materials & Supplies',
  'Equipment Maintenance',
  'Utilities',
  'Rent',
  'Transportation',
  'Labor/Wages',
  'Marketing',
  'Office Supplies',
  'Miscellaneous'
];

// Department options
const DEPARTMENTS = ['Production', 'Design', 'Sales', 'Administration', 'Delivery'];

// Employment types
const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Contract'];

export default function Finance() {
  const navigate = useNavigate();
  const { isOwner, accessToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState("monthly");
  const [selectedMonth, setSelectedMonth] = useState(() => {
    // Default to previous month if current month might not have data
    const now = new Date();
    // Check if we're in the first few days of the month
    if (now.getDate() <= 5) {
      // Use previous month as default
      const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return prevMonth.toISOString().slice(0, 7);
    }
    return now.toISOString().slice(0, 7);
  });
  const [financialData, setFinancialData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    category: '',
    description: '',
    amount: '',
    notes: ''
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Payroll state
  const [employees, setEmployees] = useState([]);
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [payrollSummary, setPayrollSummary] = useState(null);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showPayrollModal, setShowPayrollModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editingPayroll, setEditingPayroll] = useState(null);
  const [employeeForm, setEmployeeForm] = useState({
    name: '',
    position: '',
    department: 'Production',
    employment_type: 'Full-time',
    basic_salary: '',
    hourly_rate: '',
    sss_number: '',
    philhealth_number: '',
    pagibig_number: '',
    tin_number: '',
    bank_account: '',
    contact_number: '',
    date_hired: new Date().toISOString().slice(0, 10)
  });
  const [payrollForm, setPayrollForm] = useState({
    employee_id: '',
    pay_period_start: '',
    pay_period_end: '',
    days_worked: '',
    hours_worked: '',
    overtime_hours: '',
    overtime_rate: '1.25',
    basic_pay: '',
    overtime_pay: '',
    allowances: '',
    bonuses: '',
    sss_contribution: '',
    philhealth_contribution: '',
    pagibig_contribution: '',
    tax_withheld: '',
    other_deductions: '',
    deduction_notes: '',
    notes: ''
  });
  const [deleteEmployeeConfirm, setDeleteEmployeeConfirm] = useState(null);
  const [deletePayrollConfirm, setDeletePayrollConfirm] = useState(null);

  // Auth headers helper
  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${accessToken}` }
  });

  // Redirect non-admin users
  useEffect(() => {
    if (!isOwner) {
      toast.error("Access denied. Owner access only.");
      navigate("/");
    }
  }, [isOwner, navigate]);

  useEffect(() => {
    if (isOwner) {
      fetchFinancialData();
      fetchExpenses();
      fetchEmployees();
      fetchPayroll();
    }
  }, [isOwner, selectedMonth, selectedPeriod]);

  // Auto-select month with data on initial load
  const [initialMonthSet, setInitialMonthSet] = useState(false);
  
  useEffect(() => {
    if (!initialMonthSet && orders.length > 0) {
      // Find the most recent month with orders
      const monthsWithOrders = [...new Set(orders.map(o => o.created_at?.slice(0, 7)).filter(Boolean))];
      monthsWithOrders.sort().reverse();
      
      // Check if current selected month has orders
      const currentMonthHasOrders = orders.some(o => o.created_at?.startsWith(selectedMonth));
      
      if (!currentMonthHasOrders && monthsWithOrders.length > 0) {
        setSelectedMonth(monthsWithOrders[0]);
      }
      setInitialMonthSet(true);
    }
  }, [orders, initialMonthSet, selectedMonth]);

  const fetchFinancialData = async () => {
    setLoading(true);
    try {
      const [ordersRes, salesRes] = await Promise.all([
        axios.get(`${API}/orders`),
        axios.get(`${API}/reports/sales`)
      ]);

      setOrders(ordersRes.data);

      // Calculate financial metrics
      const allOrders = ordersRes.data;
      const monthOrders = allOrders.filter(o => o.created_at?.startsWith(selectedMonth));

      const totalRevenue = allOrders.reduce((sum, o) => sum + (o.total_amount || 0), 0);
      const totalCollected = allOrders.reduce((sum, o) => sum + (o.down_payment || 0) + (o.balance_payment || 0), 0);
      const totalOutstanding = allOrders.reduce((sum, o) => sum + (o.balance || 0), 0);
      
      const monthRevenue = monthOrders.reduce((sum, o) => sum + (o.total_amount || 0), 0);
      const monthCollected = monthOrders.reduce((sum, o) => sum + (o.down_payment || 0) + (o.balance_payment || 0), 0);
      const monthOutstanding = monthOrders.reduce((sum, o) => sum + (o.balance || 0), 0);

      // Payment status breakdown
      const paidOrders = allOrders.filter(o => o.payment_status === 'Paid').length;
      const partialOrders = allOrders.filter(o => o.payment_status === 'Partial').length;
      const unpaidOrders = allOrders.filter(o => o.payment_status === 'Unpaid').length;

      // Payment method breakdown
      const paymentMethods = {};
      allOrders.forEach(o => {
        const method = o.mode_of_payment || 'Not specified';
        if (!paymentMethods[method]) {
          paymentMethods[method] = { count: 0, amount: 0 };
        }
        paymentMethods[method].count++;
        paymentMethods[method].amount += (o.down_payment || 0) + (o.balance_payment || 0);
      });

      // Daily revenue data for the selected month
      const dailyRevenue = {};
      monthOrders.forEach(o => {
        const day = o.created_at?.slice(0, 10);
        if (day) {
          if (!dailyRevenue[day]) {
            dailyRevenue[day] = { revenue: 0, collected: 0, orders: 0 };
          }
          dailyRevenue[day].revenue += o.total_amount || 0;
          dailyRevenue[day].collected += (o.down_payment || 0) + (o.balance_payment || 0);
          dailyRevenue[day].orders++;
        }
      });

      // Weekly breakdown
      const weeklyData = {};
      monthOrders.forEach(o => {
        const date = new Date(o.created_at);
        const weekNum = Math.ceil(date.getDate() / 7);
        const weekKey = `Week ${weekNum}`;
        if (!weeklyData[weekKey]) {
          weeklyData[weekKey] = { revenue: 0, collected: 0, orders: 0 };
        }
        weeklyData[weekKey].revenue += o.total_amount || 0;
        weeklyData[weekKey].collected += (o.down_payment || 0) + (o.balance_payment || 0);
        weeklyData[weekKey].orders++;
      });

      setFinancialData({
        totalRevenue,
        totalCollected,
        totalOutstanding,
        monthRevenue,
        monthCollected,
        monthOutstanding,
        paidOrders,
        partialOrders,
        unpaidOrders,
        totalOrders: allOrders.length,
        monthOrders: monthOrders.length,
        paymentMethods,
        collectionRate: totalRevenue > 0 ? ((totalCollected / totalRevenue) * 100).toFixed(1) : 0,
        dailyRevenue,
        weeklyData
      });
    } catch (error) {
      console.error("Error fetching financial data:", error);
      toast.error("Failed to fetch financial data");
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenses = async () => {
    try {
      const response = await axios.get(`${API}/expenses`, getAuthHeaders());
      setExpenses(response.data || []);
    } catch (error) {
      console.error("Error fetching expenses:", error);
      // Initialize with empty array if endpoint doesn't exist yet
      setExpenses([]);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`, getAuthHeaders());
      setEmployees(response.data || []);
    } catch (error) {
      console.error("Error fetching employees:", error);
      setEmployees([]);
    }
  };

  const fetchPayroll = async () => {
    try {
      const [recordsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/payroll?month=${selectedMonth}`, getAuthHeaders()),
        axios.get(`${API}/payroll/summary/monthly?month=${selectedMonth}`, getAuthHeaders())
      ]);
      setPayrollRecords(recordsRes.data || []);
      setPayrollSummary(summaryRes.data || null);
    } catch (error) {
      console.error("Error fetching payroll:", error);
      setPayrollRecords([]);
      setPayrollSummary(null);
    }
  };

  const handleAddExpense = async () => {
    if (!expenseForm.category || !expenseForm.amount || !expenseForm.description) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const expenseData = {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount)
      };

      if (editingExpense) {
        await axios.put(`${API}/expenses/${editingExpense.id}`, expenseData, getAuthHeaders());
        toast.success("Expense updated successfully");
      } else {
        await axios.post(`${API}/expenses`, expenseData, getAuthHeaders());
        toast.success("Expense added successfully");
      }

      setShowExpenseModal(false);
      setEditingExpense(null);
      setExpenseForm({
        date: new Date().toISOString().slice(0, 10),
        category: '',
        description: '',
        amount: '',
        notes: ''
      });
      fetchExpenses();
    } catch (error) {
      console.error("Error saving expense:", error);
      toast.error("Failed to save expense");
    }
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      date: expense.date,
      category: expense.category,
      description: expense.description,
      amount: expense.amount.toString(),
      notes: expense.notes || ''
    });
    setShowExpenseModal(true);
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      await axios.delete(`${API}/expenses/${expenseId}`, getAuthHeaders());
      toast.success("Expense deleted successfully");
      setDeleteConfirm(null);
      fetchExpenses();
    } catch (error) {
      console.error("Error deleting expense:", error);
      toast.error("Failed to delete expense");
    }
  };

  // Employee handlers
  const handleAddEmployee = async () => {
    if (!employeeForm.name || !employeeForm.position || !employeeForm.basic_salary) {
      toast.error("Please fill in name, position, and basic salary");
      return;
    }

    try {
      const employeeData = {
        ...employeeForm,
        basic_salary: parseFloat(employeeForm.basic_salary) || 0,
        hourly_rate: employeeForm.hourly_rate ? parseFloat(employeeForm.hourly_rate) : null
      };

      if (editingEmployee) {
        await axios.put(`${API}/employees/${editingEmployee.id}`, employeeData, getAuthHeaders());
        toast.success("Employee updated successfully");
      } else {
        await axios.post(`${API}/employees`, employeeData, getAuthHeaders());
        toast.success("Employee added successfully");
      }

      setShowEmployeeModal(false);
      setEditingEmployee(null);
      resetEmployeeForm();
      fetchEmployees();
    } catch (error) {
      console.error("Error saving employee:", error);
      toast.error("Failed to save employee");
    }
  };

  const resetEmployeeForm = () => {
    setEmployeeForm({
      name: '',
      position: '',
      department: 'Production',
      employment_type: 'Full-time',
      basic_salary: '',
      hourly_rate: '',
      sss_number: '',
      philhealth_number: '',
      pagibig_number: '',
      tin_number: '',
      bank_account: '',
      contact_number: '',
      date_hired: new Date().toISOString().slice(0, 10)
    });
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setEmployeeForm({
      name: employee.name || '',
      position: employee.position || '',
      department: employee.department || 'Production',
      employment_type: employee.employment_type || 'Full-time',
      basic_salary: employee.basic_salary?.toString() || '',
      hourly_rate: employee.hourly_rate?.toString() || '',
      sss_number: employee.sss_number || '',
      philhealth_number: employee.philhealth_number || '',
      pagibig_number: employee.pagibig_number || '',
      tin_number: employee.tin_number || '',
      bank_account: employee.bank_account || '',
      contact_number: employee.contact_number || '',
      date_hired: employee.date_hired || new Date().toISOString().slice(0, 10)
    });
    setShowEmployeeModal(true);
  };

  const handleDeleteEmployee = async (employeeId) => {
    try {
      await axios.delete(`${API}/employees/${employeeId}`, getAuthHeaders());
      toast.success("Employee removed successfully");
      setDeleteEmployeeConfirm(null);
      fetchEmployees();
    } catch (error) {
      console.error("Error deleting employee:", error);
      toast.error("Failed to remove employee");
    }
  };

  // Payroll handlers
  const handleAddPayroll = async () => {
    if (!payrollForm.employee_id || !payrollForm.pay_period_start || !payrollForm.pay_period_end) {
      toast.error("Please select employee and pay period");
      return;
    }

    try {
      const payrollData = {
        ...payrollForm,
        days_worked: parseFloat(payrollForm.days_worked) || 0,
        hours_worked: parseFloat(payrollForm.hours_worked) || 0,
        overtime_hours: parseFloat(payrollForm.overtime_hours) || 0,
        overtime_rate: parseFloat(payrollForm.overtime_rate) || 1.25,
        basic_pay: parseFloat(payrollForm.basic_pay) || 0,
        overtime_pay: parseFloat(payrollForm.overtime_pay) || 0,
        allowances: parseFloat(payrollForm.allowances) || 0,
        bonuses: parseFloat(payrollForm.bonuses) || 0,
        sss_contribution: parseFloat(payrollForm.sss_contribution) || 0,
        philhealth_contribution: parseFloat(payrollForm.philhealth_contribution) || 0,
        pagibig_contribution: parseFloat(payrollForm.pagibig_contribution) || 0,
        tax_withheld: parseFloat(payrollForm.tax_withheld) || 0,
        other_deductions: parseFloat(payrollForm.other_deductions) || 0
      };

      if (editingPayroll) {
        await axios.put(`${API}/payroll/${editingPayroll.id}`, payrollData, getAuthHeaders());
        toast.success("Payroll record updated successfully");
      } else {
        await axios.post(`${API}/payroll`, payrollData, getAuthHeaders());
        toast.success("Payroll record created successfully");
      }

      setShowPayrollModal(false);
      setEditingPayroll(null);
      resetPayrollForm();
      fetchPayroll();
    } catch (error) {
      console.error("Error saving payroll:", error);
      toast.error("Failed to save payroll record");
    }
  };

  const resetPayrollForm = () => {
    setPayrollForm({
      employee_id: '',
      pay_period_start: '',
      pay_period_end: '',
      days_worked: '',
      hours_worked: '',
      overtime_hours: '',
      overtime_rate: '1.25',
      basic_pay: '',
      overtime_pay: '',
      allowances: '',
      bonuses: '',
      sss_contribution: '',
      philhealth_contribution: '',
      pagibig_contribution: '',
      tax_withheld: '',
      other_deductions: '',
      deduction_notes: '',
      notes: ''
    });
  };

  const handleEditPayroll = (payroll) => {
    setEditingPayroll(payroll);
    setPayrollForm({
      employee_id: payroll.employee_id || '',
      pay_period_start: payroll.pay_period_start || '',
      pay_period_end: payroll.pay_period_end || '',
      days_worked: payroll.days_worked?.toString() || '',
      hours_worked: payroll.hours_worked?.toString() || '',
      overtime_hours: payroll.overtime_hours?.toString() || '',
      overtime_rate: payroll.overtime_rate?.toString() || '1.25',
      basic_pay: payroll.basic_pay?.toString() || '',
      overtime_pay: payroll.overtime_pay?.toString() || '',
      allowances: payroll.allowances?.toString() || '',
      bonuses: payroll.bonuses?.toString() || '',
      sss_contribution: payroll.sss_contribution?.toString() || '',
      philhealth_contribution: payroll.philhealth_contribution?.toString() || '',
      pagibig_contribution: payroll.pagibig_contribution?.toString() || '',
      tax_withheld: payroll.tax_withheld?.toString() || '',
      other_deductions: payroll.other_deductions?.toString() || '',
      deduction_notes: payroll.deduction_notes || '',
      notes: payroll.notes || ''
    });
    setShowPayrollModal(true);
  };

  const handleDeletePayroll = async (payrollId) => {
    try {
      await axios.delete(`${API}/payroll/${payrollId}`, getAuthHeaders());
      toast.success("Payroll record deleted successfully");
      setDeletePayrollConfirm(null);
      fetchPayroll();
    } catch (error) {
      console.error("Error deleting payroll:", error);
      toast.error(error.response?.data?.detail || "Failed to delete payroll record");
    }
  };

  const handleUpdatePayrollStatus = async (payrollId, newStatus) => {
    try {
      await axios.patch(`${API}/payroll/${payrollId}/status?status=${newStatus}`, {}, getAuthHeaders());
      toast.success(`Payroll marked as ${newStatus}`);
      fetchPayroll();
    } catch (error) {
      console.error("Error updating payroll status:", error);
      toast.error("Failed to update status");
    }
  };

  // Calculate payroll when employee is selected
  const calculatePayroll = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    if (employee) {
      const basicPay = employee.basic_salary || 0;
      // Standard Philippine deductions (simplified)
      const sss = Math.min(basicPay * 0.045, 1350); // 4.5% employee share, max ~1350
      const philhealth = basicPay * 0.025; // 2.5% employee share
      const pagibig = Math.min(basicPay * 0.02, 100); // 2%, max 100
      
      setPayrollForm(prev => ({
        ...prev,
        basic_pay: basicPay.toString(),
        sss_contribution: sss.toFixed(2),
        philhealth_contribution: philhealth.toFixed(2),
        pagibig_contribution: pagibig.toFixed(2)
      }));
    }
  };

  const exportFinancialReport = () => {
    if (!financialData) return;

    // Calculate expense totals
    const monthExpenses = expenses.filter(e => e.date?.startsWith(selectedMonth));
    const totalExpenses = monthExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
    const profit = financialData.monthCollected - totalExpenses;

    const reportData = [
      ["Financial Report", selectedMonth],
      [""],
      ["Revenue Summary"],
      ["Total Revenue (All Time)", formatCurrency(financialData.totalRevenue)],
      ["Total Collected (All Time)", formatCurrency(financialData.totalCollected)],
      ["Total Outstanding (All Time)", formatCurrency(financialData.totalOutstanding)],
      ["Collection Rate", `${financialData.collectionRate}%`],
      [""],
      ["Monthly Summary (" + selectedMonth + ")"],
      ["Revenue", formatCurrency(financialData.monthRevenue)],
      ["Collected", formatCurrency(financialData.monthCollected)],
      ["Outstanding", formatCurrency(financialData.monthOutstanding)],
      ["Orders", financialData.monthOrders],
      [""],
      ["Expenses (" + selectedMonth + ")"],
      ["Total Expenses", formatCurrency(totalExpenses)],
      [""],
      ["Profit/Loss"],
      ["Net Profit (Collected - Expenses)", formatCurrency(profit)],
      [""],
      ["Order Status"],
      ["Paid Orders", financialData.paidOrders],
      ["Partial Orders", financialData.partialOrders],
      ["Unpaid Orders", financialData.unpaidOrders],
      [""],
      ["Expense Details"],
      ["Date", "Category", "Description", "Amount"],
      ...monthExpenses.map(e => [e.date, e.category, e.description, formatCurrency(e.amount)])
    ];

    const csvContent = reportData.map(row => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `financial_report_${selectedMonth}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported successfully");
  };

  // Print financial statement
  const printFinancialStatement = (statementType) => {
    const printWindow = window.open('', '_blank');
    
    const getStatementContent = () => {
      const monthExpensesList = expenses.filter(e => e.date?.startsWith(selectedMonth));
      const totalExpensesAmount = monthExpensesList.reduce((sum, e) => sum + (e.amount || 0), 0);
      const cogsCategories = ['Materials & Supplies', 'Labor/Wages'];
      const totalCogs = monthExpensesList.filter(e => cogsCategories.includes(e.category)).reduce((sum, e) => sum + (e.amount || 0), 0);
      const grossProfit = (financialData?.monthRevenue || 0) - totalCogs;
      const grossMargin = financialData?.monthRevenue > 0 ? ((grossProfit / financialData.monthRevenue) * 100).toFixed(1) : 0;
      const opexCategories = ['Equipment Maintenance', 'Utilities', 'Rent', 'Transportation', 'Marketing', 'Office Supplies', 'Miscellaneous'];
      const totalOpex = monthExpensesList.filter(e => opexCategories.includes(e.category)).reduce((sum, e) => sum + (e.amount || 0), 0);
      const netProfit = (financialData?.monthCollected || 0) - totalExpensesAmount;
      const netMargin = financialData?.monthCollected > 0 ? ((netProfit / financialData.monthCollected) * 100).toFixed(1) : 0;
      const totalAssets = (financialData?.totalCollected || 0) + (financialData?.totalOutstanding || 0);
      const retainedEarnings = (financialData?.totalCollected || 0) - expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
      
      const monthOrders = orders.filter(o => o.created_at?.startsWith(selectedMonth));
      const downPayments = monthOrders.reduce((sum, o) => sum + (o.down_payment || 0), 0);
      const balancePayments = monthOrders.reduce((sum, o) => sum + (o.balance_payment || 0), 0);
      
      const monthLabel = new Date(selectedMonth + "-01").toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      const currentDate = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      
      if (statementType === 'income-statement') {
        return `
          <h1>Income Statement (Profit & Loss)</h1>
          <p class="period">For the period of ${monthLabel}</p>
          
          <h2 class="section-green">Revenue</h2>
          <table>
            <tr><td>Sales Revenue (Orders)</td><td class="amount">${formatCurrency(financialData?.monthRevenue)}</td></tr>
            <tr class="sub-row"><td>&nbsp;&nbsp;- Sublimation Orders</td><td class="amount">${formatCurrency(orders.filter(o => o.created_at?.startsWith(selectedMonth) && o.printing_method === 'Sublimation').reduce((sum, o) => sum + (o.total_amount || 0), 0))}</td></tr>
            <tr class="sub-row"><td>&nbsp;&nbsp;- Embroidery Orders</td><td class="amount">${formatCurrency(orders.filter(o => o.created_at?.startsWith(selectedMonth) && o.printing_method === 'Embroidery').reduce((sum, o) => sum + (o.total_amount || 0), 0))}</td></tr>
            <tr class="total-row green"><td>Total Revenue</td><td class="amount">${formatCurrency(financialData?.monthRevenue)}</td></tr>
          </table>
          
          <h2 class="section-orange">Cost of Goods Sold (COGS)</h2>
          <table>
            ${cogsCategories.map(cat => {
              const catTotal = monthExpensesList.filter(e => e.category === cat).reduce((sum, e) => sum + (e.amount || 0), 0);
              return `<tr><td>${cat}</td><td class="amount">${formatCurrency(catTotal)}</td></tr>`;
            }).join('')}
            <tr class="total-row orange"><td>Total COGS</td><td class="amount">${formatCurrency(totalCogs)}</td></tr>
          </table>
          
          <table>
            <tr class="highlight blue"><td><strong>Gross Profit</strong></td><td class="amount"><strong>${formatCurrency(grossProfit)}</strong> (${grossMargin}% margin)</td></tr>
          </table>
          
          <h2 class="section-red">Operating Expenses</h2>
          <table>
            ${opexCategories.map(cat => {
              const catTotal = monthExpensesList.filter(e => e.category === cat).reduce((sum, e) => sum + (e.amount || 0), 0);
              if (catTotal === 0) return '';
              return `<tr><td>${cat}</td><td class="amount">${formatCurrency(catTotal)}</td></tr>`;
            }).join('')}
            <tr class="total-row red"><td>Total Operating Expenses</td><td class="amount">${formatCurrency(totalOpex)}</td></tr>
          </table>
          
          <table>
            <tr class="highlight ${netProfit >= 0 ? 'green' : 'red'}"><td><strong>Net Income (Profit/Loss)</strong></td><td class="amount"><strong>${formatCurrency(netProfit)}</strong> (${netMargin}% of collected)</td></tr>
          </table>
        `;
      } else if (statementType === 'balance-sheet') {
        return `
          <h1>Balance Sheet</h1>
          <p class="period">As of ${currentDate}</p>
          
          <div class="two-column">
            <div class="column">
              <h2 class="section-blue">ASSETS</h2>
              <h3>Current Assets</h3>
              <table>
                <tr><td>Cash & Bank (Collected)</td><td class="amount">${formatCurrency(financialData?.totalCollected)}</td></tr>
                <tr><td>Accounts Receivable</td><td class="amount">${formatCurrency(financialData?.totalOutstanding)}</td></tr>
                <tr class="total-row"><td>Total Current Assets</td><td class="amount">${formatCurrency(totalAssets)}</td></tr>
              </table>
              <table>
                <tr class="highlight blue"><td><strong>TOTAL ASSETS</strong></td><td class="amount"><strong>${formatCurrency(totalAssets)}</strong></td></tr>
              </table>
            </div>
            
            <div class="column">
              <h2 class="section-purple">LIABILITIES & EQUITY</h2>
              <h3>Current Liabilities</h3>
              <table>
                <tr><td>Accounts Payable</td><td class="amount">${formatCurrency(0)}</td></tr>
                <tr class="total-row"><td>Total Liabilities</td><td class="amount">${formatCurrency(0)}</td></tr>
              </table>
              <h3>Owner's Equity</h3>
              <table>
                <tr><td>Retained Earnings</td><td class="amount">${formatCurrency(retainedEarnings)}</td></tr>
                <tr><td>Unrealized Revenue (A/R)</td><td class="amount">${formatCurrency(financialData?.totalOutstanding)}</td></tr>
                <tr class="total-row"><td>Total Equity</td><td class="amount">${formatCurrency(totalAssets)}</td></tr>
              </table>
              <table>
                <tr class="highlight purple"><td><strong>TOTAL LIAB. & EQUITY</strong></td><td class="amount"><strong>${formatCurrency(totalAssets)}</strong></td></tr>
              </table>
            </div>
          </div>
          
          <p class="balance-check">✓ Assets = Liabilities + Equity (Balanced)</p>
        `;
      } else if (statementType === 'cash-flow') {
        const groupedExpenses = {};
        monthExpensesList.forEach(e => {
          if (!groupedExpenses[e.category]) {
            groupedExpenses[e.category] = 0;
          }
          groupedExpenses[e.category] += e.amount || 0;
        });
        
        return `
          <h1>Cash Flow Statement</h1>
          <p class="period">For the period of ${monthLabel}</p>
          
          <h2 class="section-green">Cash Flow from Operating Activities</h2>
          <h3>Cash Inflows</h3>
          <table>
            <tr><td>Down Payments Received</td><td class="amount green">+ ${formatCurrency(downPayments)}</td></tr>
            <tr><td>Balance Payments Received</td><td class="amount green">+ ${formatCurrency(balancePayments)}</td></tr>
            <tr class="total-row"><td>Total Cash Inflows</td><td class="amount">${formatCurrency(downPayments + balancePayments)}</td></tr>
          </table>
          
          <h3>Cash Outflows</h3>
          <table>
            ${Object.entries(groupedExpenses).map(([cat, amount]) => 
              `<tr><td>${cat}</td><td class="amount red">- ${formatCurrency(amount)}</td></tr>`
            ).join('')}
            ${Object.keys(groupedExpenses).length === 0 ? '<tr><td colspan="2" style="text-align:center">No expenses recorded</td></tr>' : ''}
            <tr class="total-row"><td>Total Cash Outflows</td><td class="amount red">- ${formatCurrency(totalExpensesAmount)}</td></tr>
          </table>
          
          <table>
            <tr class="highlight ${netProfit >= 0 ? 'green' : 'red'}"><td><strong>Net Cash from Operations</strong></td><td class="amount"><strong>${formatCurrency(netProfit)}</strong></td></tr>
          </table>
          
          <h2 class="section-blue">Cash Flow from Investing Activities</h2>
          <table>
            <tr><td colspan="2" style="text-align:center">No investing activities recorded</td></tr>
            <tr class="total-row"><td>Net Cash from Investing</td><td class="amount">${formatCurrency(0)}</td></tr>
          </table>
          
          <h2 class="section-purple">Cash Flow from Financing Activities</h2>
          <table>
            <tr><td colspan="2" style="text-align:center">No financing activities recorded</td></tr>
            <tr class="total-row"><td>Net Cash from Financing</td><td class="amount">${formatCurrency(0)}</td></tr>
          </table>
          
          <table>
            <tr class="highlight ${netProfit >= 0 ? 'green' : 'red'}"><td><strong>Net Change in Cash</strong></td><td class="amount"><strong>${formatCurrency(netProfit)}</strong></td></tr>
          </table>
          
          <div class="cash-position">
            <div>Beginning Cash<br/><strong>${formatCurrency((financialData?.totalCollected || 0) - (financialData?.monthCollected || 0))}</strong></div>
            <div>+ Net Change<br/><strong class="${netProfit >= 0 ? 'green' : 'red'}">${formatCurrency(netProfit)}</strong></div>
            <div>= Ending Cash<br/><strong class="blue">${formatCurrency((financialData?.totalCollected || 0) - totalExpensesAmount)}</strong></div>
          </div>
        `;
      }
      return '';
    };
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Financial Statement - ${statementType}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 30px; max-width: 900px; margin: 0 auto; }
            h1 { text-align: center; color: #1e3a5f; margin-bottom: 5px; }
            h2 { margin-top: 25px; margin-bottom: 10px; padding: 8px 12px; border-radius: 4px; font-size: 14px; }
            h3 { margin: 15px 0 8px 0; font-size: 13px; color: #666; }
            .period { text-align: center; color: #666; margin-bottom: 25px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
            td { padding: 8px 12px; border-bottom: 1px solid #e5e5e5; }
            .amount { text-align: right; font-family: monospace; }
            .sub-row td { color: #666; font-size: 13px; }
            .total-row { background-color: #f5f5f5; font-weight: bold; }
            .total-row.green { background-color: #dcfce7; color: #166534; }
            .total-row.orange { background-color: #ffedd5; color: #9a3412; }
            .total-row.red { background-color: #fee2e2; color: #dc2626; }
            .highlight { font-size: 16px; }
            .highlight.green { background-color: #dcfce7; }
            .highlight.red { background-color: #fee2e2; }
            .highlight.blue { background-color: #dbeafe; }
            .highlight.purple { background-color: #f3e8ff; }
            .section-green { background-color: #dcfce7; color: #166534; }
            .section-orange { background-color: #ffedd5; color: #9a3412; }
            .section-red { background-color: #fee2e2; color: #dc2626; }
            .section-blue { background-color: #dbeafe; color: #1e40af; }
            .section-purple { background-color: #f3e8ff; color: #7c3aed; }
            .green { color: #166534; }
            .red { color: #dc2626; }
            .blue { color: #1e40af; }
            .two-column { display: flex; gap: 30px; }
            .column { flex: 1; }
            .balance-check { text-align: center; padding: 10px; background-color: #f0f0f0; border-radius: 4px; margin-top: 20px; }
            .cash-position { display: flex; justify-content: space-around; background-color: #f5f5f5; padding: 15px; border-radius: 8px; margin-top: 20px; text-align: center; }
            .footer { text-align: center; margin-top: 30px; padding-top: 15px; border-top: 1px solid #ddd; color: #888; font-size: 12px; }
            @media print { 
              body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } 
              .two-column { display: flex; }
            }
          </style>
        </head>
        <body>
          ${getStatementContent()}
          <div class="footer">
            <p>Generated on ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // Generate month options
  const getMonthOptions = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(date.toISOString().slice(0, 7));
    }
    return months;
  };

  // Calculate expense totals for selected month
  const monthExpenses = expenses.filter(e => e.date?.startsWith(selectedMonth));
  const totalMonthExpenses = monthExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const monthProfit = (financialData?.monthCollected || 0) - totalMonthExpenses;
  const profitMargin = financialData?.monthCollected > 0 
    ? ((monthProfit / financialData.monthCollected) * 100).toFixed(1) 
    : 0;

  // Group expenses by category
  const expensesByCategory = {};
  monthExpenses.forEach(e => {
    if (!expensesByCategory[e.category]) {
      expensesByCategory[e.category] = 0;
    }
    expensesByCategory[e.category] += e.amount || 0;
  });

  // Today's expenses
  const today = new Date().toISOString().slice(0, 10);
  const todayExpenses = expenses.filter(e => e.date === today);
  const totalTodayExpenses = todayExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);

  if (!isOwner) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="bg-white/80 backdrop-blur-md border-b border-border sticky top-0 z-40">
          <div className="container mx-auto px-6 py-4">
            <Skeleton className="h-8 w-64" />
          </div>
        </header>
        <main className="container mx-auto px-6 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-border sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/")}
                data-testid="back-button"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-extrabold tracking-tight text-primary">
                  Finance & Accounting
                </h1>
                <p className="text-sm text-muted-foreground">Owner access only - Financial overview, expenses, and profit tracking</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-40" data-testid="month-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getMonthOptions().map(month => (
                    <SelectItem key={month} value={month}>
                      {new Date(month + "-01").toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={exportFinancialReport} variant="outline" data-testid="export-report">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card data-testid="total-revenue-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Month Revenue</CardTitle>
              <DollarSign className="h-5 w-5 text-green-600" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(financialData?.monthRevenue)}</p>
              <p className="text-xs text-muted-foreground mt-1">{financialData?.monthOrders} orders</p>
            </CardContent>
          </Card>

          <Card data-testid="collected-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Month Collected</CardTitle>
              <Wallet className="h-5 w-5 text-blue-600" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(financialData?.monthCollected)}</p>
              <p className="text-xs text-muted-foreground mt-1">{financialData?.collectionRate}% rate</p>
            </CardContent>
          </Card>

          <Card data-testid="expenses-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Month Expenses</CardTitle>
              <Receipt className="h-5 w-5 text-red-600" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(totalMonthExpenses)}</p>
              <p className="text-xs text-muted-foreground mt-1">{monthExpenses.length} entries</p>
            </CardContent>
          </Card>

          <Card data-testid="profit-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Net Profit</CardTitle>
              {monthProfit >= 0 ? (
                <TrendingUp className="h-5 w-5 text-emerald-600" />
              ) : (
                <TrendingDown className="h-5 w-5 text-red-600" />
              )}
            </CardHeader>
            <CardContent>
              <p className={`text-2xl font-bold ${monthProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {formatCurrency(monthProfit)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{profitMargin}% margin</p>
            </CardContent>
          </Card>

          <Card data-testid="outstanding-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Outstanding</CardTitle>
              <Clock className="h-5 w-5 text-amber-600" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-amber-600">{formatCurrency(financialData?.monthOutstanding)}</p>
              <p className="text-xs text-muted-foreground mt-1">To collect</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for detailed views */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="flex-wrap">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="income-statement" data-testid="tab-income-statement">Income Statement</TabsTrigger>
            <TabsTrigger value="balance-sheet" data-testid="tab-balance-sheet">Balance Sheet</TabsTrigger>
            <TabsTrigger value="cash-flow" data-testid="tab-cash-flow">Cash Flow</TabsTrigger>
            <TabsTrigger value="expenses" data-testid="tab-expenses">Daily Expenses</TabsTrigger>
            <TabsTrigger value="receivables" data-testid="tab-receivables">Receivables</TabsTrigger>
            <TabsTrigger value="collections" data-testid="tab-collections">Collections</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Payment Status Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PiggyBank className="h-5 w-5" />
                    Payment Status Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <span>Paid</span>
                      </div>
                      <span className="font-semibold">{financialData?.paidOrders} orders</span>
                    </div>
                    <div className="w-full bg-secondary rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full" 
                        style={{ width: `${(financialData?.paidOrders / financialData?.totalOrders) * 100 || 0}%` }}
                      ></div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                        <span>Partial</span>
                      </div>
                      <span className="font-semibold">{financialData?.partialOrders} orders</span>
                    </div>
                    <div className="w-full bg-secondary rounded-full h-2">
                      <div 
                        className="bg-amber-500 h-2 rounded-full" 
                        style={{ width: `${(financialData?.partialOrders / financialData?.totalOrders) * 100 || 0}%` }}
                      ></div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                        <span>Unpaid</span>
                      </div>
                      <span className="font-semibold">{financialData?.unpaidOrders} orders</span>
                    </div>
                    <div className="w-full bg-secondary rounded-full h-2">
                      <div 
                        className="bg-red-500 h-2 rounded-full" 
                        style={{ width: `${(financialData?.unpaidOrders / financialData?.totalOrders) * 100 || 0}%` }}
                      ></div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Profit Analysis */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Banknote className="h-5 w-5" />
                    Profit Analysis ({new Date(selectedMonth + "-01").toLocaleDateString('en-US', { month: 'long', year: 'numeric' })})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Collected Revenue</span>
                        <span className="font-semibold text-blue-600">{formatCurrency(financialData?.monthCollected)}</span>
                      </div>
                      <div className="w-full bg-secondary rounded-full h-3">
                        <div className="bg-blue-500 h-3 rounded-full" style={{ width: '100%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Total Expenses</span>
                        <span className="font-semibold text-red-600">- {formatCurrency(totalMonthExpenses)}</span>
                      </div>
                      <div className="w-full bg-secondary rounded-full h-3">
                        <div 
                          className="bg-red-500 h-3 rounded-full" 
                          style={{ width: `${financialData?.monthCollected > 0 ? Math.min((totalMonthExpenses / financialData.monthCollected) * 100, 100) : 0}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="border-t pt-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground font-medium">Net Profit</span>
                        <span className={`font-bold text-lg ${monthProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatCurrency(monthProfit)}
                        </span>
                      </div>
                      <div className="w-full bg-secondary rounded-full h-3">
                        <div 
                          className={`h-3 rounded-full ${monthProfit >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`}
                          style={{ width: `${financialData?.monthCollected > 0 ? Math.abs((monthProfit / financialData.monthCollected) * 100) : 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Expense Breakdown by Category */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="h-5 w-5" />
                    Expense Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.keys(expensesByCategory).length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Receipt className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No expenses recorded for this month</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {Object.entries(expensesByCategory)
                        .sort((a, b) => b[1] - a[1])
                        .map(([category, amount]) => (
                          <div key={category} className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                            <span className="text-sm font-medium">{category}</span>
                            <span className="font-semibold text-red-600">{formatCurrency(amount)}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Weekly Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Weekly Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.keys(financialData?.weeklyData || {}).length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No data for this month</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {Object.entries(financialData?.weeklyData || {})
                        .sort((a, b) => a[0].localeCompare(b[0]))
                        .map(([week, data]) => (
                          <div key={week} className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                            <div>
                              <p className="font-medium">{week}</p>
                              <p className="text-xs text-muted-foreground">{data.orders} orders</p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-green-600">{formatCurrency(data.revenue)}</p>
                              <p className="text-xs text-blue-600">{formatCurrency(data.collected)} collected</p>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Income Statement Tab */}
          <TabsContent value="income-statement">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    Income Statement (Profit & Loss)
                  </CardTitle>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">
                      {new Date(selectedMonth + "-01").toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => printFinancialStatement('income-statement')}
                      data-testid="print-income-statement"
                    >
                      <Printer className="h-4 w-4 mr-2" />
                      Print
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Revenue Section */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-green-600" />
                      Revenue
                    </h3>
                    <div className="bg-green-50 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between">
                        <span>Sales Revenue (Orders)</span>
                        <span className="font-semibold">{formatCurrency(financialData?.monthRevenue)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span className="pl-4">- Sublimation Orders</span>
                        <span>{formatCurrency(orders.filter(o => o.created_at?.startsWith(selectedMonth) && o.printing_method === 'Sublimation').reduce((sum, o) => sum + (o.total_amount || 0), 0))}</span>
                      </div>
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span className="pl-4">- Embroidery Orders</span>
                        <span>{formatCurrency(orders.filter(o => o.created_at?.startsWith(selectedMonth) && o.printing_method === 'Embroidery').reduce((sum, o) => sum + (o.total_amount || 0), 0))}</span>
                      </div>
                      <div className="border-t border-green-200 pt-2 mt-2 flex justify-between font-bold text-green-700">
                        <span>Total Revenue</span>
                        <span>{formatCurrency(financialData?.monthRevenue)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Cost of Goods Sold */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <Coins className="h-5 w-5 text-orange-600" />
                      Cost of Goods Sold (COGS)
                    </h3>
                    <div className="bg-orange-50 rounded-lg p-4 space-y-2">
                      {(() => {
                        const cogsCategories = ['Materials & Supplies', 'Labor/Wages'];
                        const cogsExpenses = monthExpenses.filter(e => cogsCategories.includes(e.category));
                        const totalCogs = cogsExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
                        return (
                          <>
                            {cogsCategories.map(cat => {
                              const catTotal = cogsExpenses.filter(e => e.category === cat).reduce((sum, e) => sum + (e.amount || 0), 0);
                              return (
                                <div key={cat} className="flex justify-between">
                                  <span>{cat}</span>
                                  <span className="font-semibold">{formatCurrency(catTotal)}</span>
                                </div>
                              );
                            })}
                            <div className="border-t border-orange-200 pt-2 mt-2 flex justify-between font-bold text-orange-700">
                              <span>Total COGS</span>
                              <span>{formatCurrency(totalCogs)}</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Gross Profit */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    {(() => {
                      const cogsCategories = ['Materials & Supplies', 'Labor/Wages'];
                      const totalCogs = monthExpenses.filter(e => cogsCategories.includes(e.category)).reduce((sum, e) => sum + (e.amount || 0), 0);
                      const grossProfit = (financialData?.monthRevenue || 0) - totalCogs;
                      const grossMargin = financialData?.monthRevenue > 0 ? ((grossProfit / financialData.monthRevenue) * 100).toFixed(1) : 0;
                      return (
                        <div className="flex justify-between font-bold text-lg text-blue-700">
                          <span>Gross Profit</span>
                          <div className="text-right">
                            <span>{formatCurrency(grossProfit)}</span>
                            <span className="text-sm font-normal ml-2">({grossMargin}% margin)</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Operating Expenses */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <Receipt className="h-5 w-5 text-red-600" />
                      Operating Expenses
                    </h3>
                    <div className="bg-red-50 rounded-lg p-4 space-y-2">
                      {(() => {
                        const opexCategories = ['Equipment Maintenance', 'Utilities', 'Rent', 'Transportation', 'Marketing', 'Office Supplies', 'Miscellaneous'];
                        const opexExpenses = monthExpenses.filter(e => opexCategories.includes(e.category));
                        const totalOpex = opexExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
                        return (
                          <>
                            {opexCategories.map(cat => {
                              const catTotal = opexExpenses.filter(e => e.category === cat).reduce((sum, e) => sum + (e.amount || 0), 0);
                              if (catTotal === 0) return null;
                              return (
                                <div key={cat} className="flex justify-between">
                                  <span>{cat}</span>
                                  <span className="font-semibold">{formatCurrency(catTotal)}</span>
                                </div>
                              );
                            })}
                            {totalOpex === 0 && (
                              <div className="text-center text-muted-foreground py-2">No operating expenses recorded</div>
                            )}
                            <div className="border-t border-red-200 pt-2 mt-2 flex justify-between font-bold text-red-700">
                              <span>Total Operating Expenses</span>
                              <span>{formatCurrency(totalOpex)}</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Net Income */}
                  <div className={`rounded-lg p-4 ${monthProfit >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                    <div className={`flex justify-between font-bold text-xl ${monthProfit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      <span>Net Income (Profit/Loss)</span>
                      <div className="text-right">
                        <span>{formatCurrency(monthProfit)}</span>
                        <span className="text-sm font-normal ml-2">({profitMargin}% of collected)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Balance Sheet Tab */}
          <TabsContent value="balance-sheet">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Scale className="h-5 w-5 text-purple-600" />
                    Balance Sheet
                  </CardTitle>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">
                      As of {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => printFinancialStatement('balance-sheet')}
                      data-testid="print-balance-sheet"
                    >
                      <Printer className="h-4 w-4 mr-2" />
                      Print
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Assets */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-blue-700">
                      <Building2 className="h-5 w-5" />
                      ASSETS
                    </h3>
                    
                    {/* Current Assets */}
                    <div className="bg-blue-50 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold mb-3 text-blue-600">Current Assets</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span>Cash & Bank (Collected)</span>
                          <span className="font-semibold">{formatCurrency(financialData?.totalCollected)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Accounts Receivable</span>
                          <span className="font-semibold">{formatCurrency(financialData?.totalOutstanding)}</span>
                        </div>
                        <div className="border-t border-blue-200 pt-2 mt-2 flex justify-between font-bold">
                          <span>Total Current Assets</span>
                          <span>{formatCurrency((financialData?.totalCollected || 0) + (financialData?.totalOutstanding || 0))}</span>
                        </div>
                      </div>
                    </div>

                    {/* Total Assets */}
                    <div className="bg-blue-100 rounded-lg p-4">
                      <div className="flex justify-between font-bold text-lg text-blue-700">
                        <span>TOTAL ASSETS</span>
                        <span>{formatCurrency((financialData?.totalCollected || 0) + (financialData?.totalOutstanding || 0))}</span>
                      </div>
                    </div>
                  </div>

                  {/* Liabilities & Equity */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-purple-700">
                      <CircleDollarSign className="h-5 w-5" />
                      LIABILITIES & EQUITY
                    </h3>
                    
                    {/* Liabilities */}
                    <div className="bg-amber-50 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold mb-3 text-amber-600">Current Liabilities</h4>
                      <div className="space-y-2">
                        {(() => {
                          const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
                          return (
                            <>
                              <div className="flex justify-between">
                                <span>Accounts Payable</span>
                                <span className="font-semibold">{formatCurrency(0)}</span>
                              </div>
                              <div className="flex justify-between text-sm text-muted-foreground">
                                <span className="pl-4">(Expenses already paid)</span>
                                <span>{formatCurrency(totalExpenses)}</span>
                              </div>
                            </>
                          );
                        })()}
                        <div className="border-t border-amber-200 pt-2 mt-2 flex justify-between font-bold">
                          <span>Total Liabilities</span>
                          <span>{formatCurrency(0)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Owner's Equity */}
                    <div className="bg-emerald-50 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold mb-3 text-emerald-600">Owner's Equity</h4>
                      <div className="space-y-2">
                        {(() => {
                          const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
                          const retainedEarnings = (financialData?.totalCollected || 0) - totalExpenses;
                          const totalAssets = (financialData?.totalCollected || 0) + (financialData?.totalOutstanding || 0);
                          return (
                            <>
                              <div className="flex justify-between">
                                <span>Retained Earnings</span>
                                <span className="font-semibold">{formatCurrency(retainedEarnings)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Unrealized Revenue (A/R)</span>
                                <span className="font-semibold">{formatCurrency(financialData?.totalOutstanding)}</span>
                              </div>
                              <div className="border-t border-emerald-200 pt-2 mt-2 flex justify-between font-bold">
                                <span>Total Equity</span>
                                <span>{formatCurrency(totalAssets)}</span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Total Liabilities & Equity */}
                    <div className="bg-purple-100 rounded-lg p-4">
                      <div className="flex justify-between font-bold text-lg text-purple-700">
                        <span>TOTAL LIAB. & EQUITY</span>
                        <span>{formatCurrency((financialData?.totalCollected || 0) + (financialData?.totalOutstanding || 0))}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Balance Check */}
                <div className="mt-6 p-4 bg-gray-100 rounded-lg">
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span className="text-muted-foreground">Assets = Liabilities + Equity (Balanced)</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cash Flow Statement Tab */}
          <TabsContent value="cash-flow">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <ArrowUpDown className="h-5 w-5 text-cyan-600" />
                    Cash Flow Statement
                  </CardTitle>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">
                      {new Date(selectedMonth + "-01").toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => printFinancialStatement('cash-flow')}
                      data-testid="print-cash-flow"
                    >
                      <Printer className="h-4 w-4 mr-2" />
                      Print
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Cash Flow from Operating Activities */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-green-600" />
                      Cash Flow from Operating Activities
                    </h3>
                    <div className="bg-green-50 rounded-lg p-4 space-y-3">
                      {/* Cash Inflows */}
                      <div>
                        <h4 className="font-medium text-green-700 mb-2">Cash Inflows</h4>
                        <div className="space-y-2 pl-4">
                          {(() => {
                            const monthOrders = orders.filter(o => o.created_at?.startsWith(selectedMonth));
                            const downPayments = monthOrders.reduce((sum, o) => sum + (o.down_payment || 0), 0);
                            const balancePayments = monthOrders.reduce((sum, o) => sum + (o.balance_payment || 0), 0);
                            return (
                              <>
                                <div className="flex justify-between">
                                  <span>Down Payments Received</span>
                                  <span className="font-semibold text-green-600">+ {formatCurrency(downPayments)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Balance Payments Received</span>
                                  <span className="font-semibold text-green-600">+ {formatCurrency(balancePayments)}</span>
                                </div>
                                <div className="border-t border-green-200 pt-2 mt-2 flex justify-between font-semibold">
                                  <span>Total Cash Inflows</span>
                                  <span className="text-green-700">{formatCurrency(downPayments + balancePayments)}</span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Cash Outflows */}
                      <div>
                        <h4 className="font-medium text-red-700 mb-2">Cash Outflows</h4>
                        <div className="space-y-2 pl-4">
                          {(() => {
                            const groupedExpenses = {};
                            monthExpenses.forEach(e => {
                              if (!groupedExpenses[e.category]) {
                                groupedExpenses[e.category] = 0;
                              }
                              groupedExpenses[e.category] += e.amount || 0;
                            });
                            return (
                              <>
                                {Object.entries(groupedExpenses).map(([cat, amount]) => (
                                  <div key={cat} className="flex justify-between">
                                    <span>{cat}</span>
                                    <span className="font-semibold text-red-600">- {formatCurrency(amount)}</span>
                                  </div>
                                ))}
                                {Object.keys(groupedExpenses).length === 0 && (
                                  <div className="text-muted-foreground text-sm">No expenses recorded</div>
                                )}
                                <div className="border-t border-red-200 pt-2 mt-2 flex justify-between font-semibold">
                                  <span>Total Cash Outflows</span>
                                  <span className="text-red-700">- {formatCurrency(totalMonthExpenses)}</span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Net Cash from Operations */}
                      <div className="border-t-2 border-green-300 pt-3">
                        <div className={`flex justify-between font-bold text-lg ${monthProfit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          <span>Net Cash from Operations</span>
                          <span>{formatCurrency(monthProfit)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cash Flow from Investing Activities */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-blue-600" />
                      Cash Flow from Investing Activities
                    </h3>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-center text-muted-foreground py-4">
                        <p>No investing activities recorded</p>
                        <p className="text-xs mt-1">(Equipment purchases, asset sales, etc.)</p>
                      </div>
                      <div className="border-t border-blue-200 pt-2 mt-2 flex justify-between font-bold">
                        <span>Net Cash from Investing</span>
                        <span>{formatCurrency(0)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Cash Flow from Financing Activities */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <Banknote className="h-5 w-5 text-purple-600" />
                      Cash Flow from Financing Activities
                    </h3>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="text-center text-muted-foreground py-4">
                        <p>No financing activities recorded</p>
                        <p className="text-xs mt-1">(Loans, owner investments/withdrawals, etc.)</p>
                      </div>
                      <div className="border-t border-purple-200 pt-2 mt-2 flex justify-between font-bold">
                        <span>Net Cash from Financing</span>
                        <span>{formatCurrency(0)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Net Change in Cash */}
                  <div className={`rounded-lg p-4 ${monthProfit >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                    <div className={`flex justify-between font-bold text-xl ${monthProfit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      <span>Net Change in Cash</span>
                      <span>{formatCurrency(monthProfit)}</span>
                    </div>
                    <div className="text-sm text-muted-foreground mt-2 text-center">
                      Operating ({formatCurrency(monthProfit)}) + Investing ({formatCurrency(0)}) + Financing ({formatCurrency(0)})
                    </div>
                  </div>

                  {/* Cash Position */}
                  <div className="bg-gray-100 rounded-lg p-4">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-sm text-muted-foreground">Beginning Cash</p>
                        <p className="text-lg font-bold">{formatCurrency((financialData?.totalCollected || 0) - (financialData?.monthCollected || 0))}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">+ Net Change</p>
                        <p className={`text-lg font-bold ${monthProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(monthProfit)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">= Ending Cash</p>
                        <p className="text-lg font-bold text-blue-600">{formatCurrency((financialData?.totalCollected || 0) - totalMonthExpenses)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Daily Expenses Tab */}
          <TabsContent value="expenses">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="h-5 w-5 text-red-600" />
                    Daily Expenses
                  </CardTitle>
                  <Button 
                    onClick={() => {
                      setEditingExpense(null);
                      setExpenseForm({
                        date: new Date().toISOString().slice(0, 10),
                        category: '',
                        description: '',
                        amount: '',
                        notes: ''
                      });
                      setShowExpenseModal(true);
                    }}
                    data-testid="add-expense-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Expense
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Today's Quick Summary */}
                <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border border-red-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">Today's Expenses</h3>
                      <p className="text-sm text-muted-foreground">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-red-600">{formatCurrency(totalTodayExpenses)}</p>
                      <p className="text-xs text-muted-foreground">{todayExpenses.length} entries</p>
                    </div>
                  </div>
                </div>

                {/* Expenses Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary">
                      <tr>
                        <th className="text-left p-3">Date</th>
                        <th className="text-left p-3">Category</th>
                        <th className="text-left p-3">Description</th>
                        <th className="text-right p-3">Amount</th>
                        <th className="text-center p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthExpenses.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center py-12 text-muted-foreground">
                            <Receipt className="h-12 w-12 mx-auto mb-2 opacity-50" />
                            <p>No expenses recorded for this month</p>
                            <p className="text-sm">Click "Add Expense" to record your first expense</p>
                          </td>
                        </tr>
                      ) : (
                        monthExpenses
                          .sort((a, b) => new Date(b.date) - new Date(a.date))
                          .map((expense) => (
                            <tr key={expense.id} className="border-t border-border hover:bg-secondary/50">
                              <td className="p-3">{formatDate(expense.date)}</td>
                              <td className="p-3">
                                <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">
                                  {expense.category}
                                </span>
                              </td>
                              <td className="p-3">
                                <p className="font-medium">{expense.description}</p>
                                {expense.notes && (
                                  <p className="text-xs text-muted-foreground">{expense.notes}</p>
                                )}
                              </td>
                              <td className="p-3 text-right font-semibold text-red-600">
                                {formatCurrency(expense.amount)}
                              </td>
                              <td className="p-3 text-center">
                                <div className="flex justify-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditExpense(expense)}
                                    data-testid={`edit-expense-${expense.id}`}
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => setDeleteConfirm(expense)}
                                    data-testid={`delete-expense-${expense.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                      )}
                    </tbody>
                    {monthExpenses.length > 0 && (
                      <tfoot className="bg-secondary font-semibold">
                        <tr>
                          <td colSpan="3" className="p-3 text-right">Total:</td>
                          <td className="p-3 text-right text-red-600">{formatCurrency(totalMonthExpenses)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Receivables Tab */}
          <TabsContent value="receivables">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingDown className="h-5 w-5 text-amber-600" />
                  Outstanding Receivables
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary">
                      <tr>
                        <th className="text-left p-3">Client</th>
                        <th className="text-left p-3">Order Date</th>
                        <th className="text-right p-3">Total Amount</th>
                        <th className="text-right p-3">Paid</th>
                        <th className="text-right p-3">Balance</th>
                        <th className="text-center p-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders
                        .filter(o => o.balance > 0)
                        .sort((a, b) => b.balance - a.balance)
                        .slice(0, 20)
                        .map((order) => (
                          <tr key={order.id} className="border-t border-border hover:bg-secondary/50">
                            <td className="p-3 font-medium">{order.client_name}</td>
                            <td className="p-3">{order.created_at?.slice(0, 10)}</td>
                            <td className="p-3 text-right">{formatCurrency(order.total_amount)}</td>
                            <td className="p-3 text-right text-green-600">{formatCurrency((order.down_payment || 0) + (order.balance_payment || 0))}</td>
                            <td className="p-3 text-right font-semibold text-amber-600">{formatCurrency(order.balance)}</td>
                            <td className="p-3 text-center">
                              <span className={`text-xs px-2 py-1 rounded ${
                                order.payment_status === 'Partial' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {order.payment_status}
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                    <tfoot className="bg-secondary font-semibold">
                      <tr>
                        <td colSpan="4" className="p-3 text-right">Total Outstanding:</td>
                        <td className="p-3 text-right text-amber-600">{formatCurrency(financialData?.totalOutstanding)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Collections Tab */}
          <TabsContent value="collections">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-blue-600" />
                  Recent Collections
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary">
                      <tr>
                        <th className="text-left p-3">Client</th>
                        <th className="text-left p-3">Type</th>
                        <th className="text-left p-3">Date</th>
                        <th className="text-right p-3">Amount</th>
                        <th className="text-left p-3">Method</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders
                        .flatMap(order => 
                          (order.payment_history || []).map(payment => ({
                            ...payment,
                            client_name: order.client_name,
                            order_id: order.id
                          }))
                        )
                        .sort((a, b) => new Date(b.payment_date || b.recorded_at) - new Date(a.payment_date || a.recorded_at))
                        .slice(0, 30)
                        .map((payment, idx) => (
                          <tr key={idx} className="border-t border-border hover:bg-secondary/50">
                            <td className="p-3 font-medium">{payment.client_name}</td>
                            <td className="p-3">
                              <span className={`text-xs px-2 py-1 rounded ${
                                payment.type === 'Down Payment' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                              }`}>
                                {payment.type}
                              </span>
                            </td>
                            <td className="p-3">{payment.payment_date || payment.recorded_at?.slice(0, 10)}</td>
                            <td className="p-3 text-right font-semibold text-green-600">{formatCurrency(payment.amount)}</td>
                            <td className="p-3">{payment.mode_of_payment || '-'}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
      </main>

      {/* Add/Edit Expense Modal */}
      <Dialog open={showExpenseModal} onOpenChange={setShowExpenseModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingExpense ? 'Edit Expense' : 'Add New Expense'}</DialogTitle>
            <DialogDescription>
              {editingExpense ? 'Update the expense details below.' : 'Record a new business expense.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="expense-date">Date *</Label>
              <Input
                id="expense-date"
                type="date"
                value={expenseForm.date}
                onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                data-testid="expense-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expense-category">Category *</Label>
              <Select 
                value={expenseForm.category} 
                onValueChange={(value) => setExpenseForm({ ...expenseForm, category: value })}
              >
                <SelectTrigger data-testid="expense-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="expense-description">Description *</Label>
              <Input
                id="expense-description"
                placeholder="e.g., Ink cartridges, Electricity bill"
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                data-testid="expense-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expense-amount">Amount (PHP) *</Label>
              <Input
                id="expense-amount"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={expenseForm.amount}
                onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                data-testid="expense-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expense-notes">Notes (optional)</Label>
              <Textarea
                id="expense-notes"
                placeholder="Additional notes about this expense"
                value={expenseForm.notes}
                onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                data-testid="expense-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExpenseModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddExpense} data-testid="save-expense-btn">
              {editingExpense ? 'Update' : 'Add'} Expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Delete Expense
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this expense? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteConfirm && (
            <div className="py-4 px-4 bg-secondary/50 rounded-lg">
              <p className="font-medium">{deleteConfirm.description}</p>
              <p className="text-sm text-muted-foreground">{deleteConfirm.category} - {formatDate(deleteConfirm.date)}</p>
              <p className="text-lg font-bold text-red-600 mt-2">{formatCurrency(deleteConfirm.amount)}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDeleteExpense(deleteConfirm?.id)}
              data-testid="confirm-delete-expense"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Employee Modal */}
      <Dialog open={showEmployeeModal} onOpenChange={setShowEmployeeModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
            <DialogDescription>
              {editingEmployee ? 'Update employee information.' : 'Add a new employee to the payroll system.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="emp-name">Full Name *</Label>
                <Input
                  id="emp-name"
                  placeholder="Juan Dela Cruz"
                  value={employeeForm.name}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, name: e.target.value })}
                  data-testid="employee-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emp-position">Position *</Label>
                <Input
                  id="emp-position"
                  placeholder="e.g., Production Staff"
                  value={employeeForm.position}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, position: e.target.value })}
                  data-testid="employee-position-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Department</Label>
                <Select 
                  value={employeeForm.department} 
                  onValueChange={(value) => setEmployeeForm({ ...employeeForm, department: value })}
                >
                  <SelectTrigger data-testid="employee-department-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTMENTS.map(dept => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Employment Type</Label>
                <Select 
                  value={employeeForm.employment_type} 
                  onValueChange={(value) => setEmployeeForm({ ...employeeForm, employment_type: value })}
                >
                  <SelectTrigger data-testid="employee-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EMPLOYMENT_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="emp-salary">Basic Salary (Monthly) *</Label>
                <Input
                  id="emp-salary"
                  type="number"
                  placeholder="0.00"
                  value={employeeForm.basic_salary}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, basic_salary: e.target.value })}
                  data-testid="employee-salary-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emp-hourly">Hourly Rate (Optional)</Label>
                <Input
                  id="emp-hourly"
                  type="number"
                  placeholder="0.00"
                  value={employeeForm.hourly_rate}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, hourly_rate: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="emp-hired">Date Hired</Label>
              <Input
                id="emp-hired"
                type="date"
                value={employeeForm.date_hired}
                onChange={(e) => setEmployeeForm({ ...employeeForm, date_hired: e.target.value })}
              />
            </div>
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-muted-foreground mb-3">Government IDs</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="emp-sss">SSS Number</Label>
                  <Input
                    id="emp-sss"
                    placeholder="00-0000000-0"
                    value={employeeForm.sss_number}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, sss_number: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emp-philhealth">PhilHealth Number</Label>
                  <Input
                    id="emp-philhealth"
                    placeholder="00-000000000-0"
                    value={employeeForm.philhealth_number}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, philhealth_number: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emp-pagibig">Pag-IBIG Number</Label>
                  <Input
                    id="emp-pagibig"
                    placeholder="0000-0000-0000"
                    value={employeeForm.pagibig_number}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, pagibig_number: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emp-tin">TIN</Label>
                  <Input
                    id="emp-tin"
                    placeholder="000-000-000-000"
                    value={employeeForm.tin_number}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, tin_number: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="emp-bank">Bank Account</Label>
                <Input
                  id="emp-bank"
                  placeholder="Account number"
                  value={employeeForm.bank_account}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, bank_account: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emp-contact">Contact Number</Label>
                <Input
                  id="emp-contact"
                  placeholder="09XX XXX XXXX"
                  value={employeeForm.contact_number}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, contact_number: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmployeeModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddEmployee} data-testid="save-employee-btn">
              {editingEmployee ? 'Update' : 'Add'} Employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Payroll Modal */}
      <Dialog open={showPayrollModal} onOpenChange={setShowPayrollModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPayroll ? 'Edit Payroll Record' : 'New Payroll Record'}</DialogTitle>
            <DialogDescription>
              {editingPayroll ? 'Update payroll details.' : 'Create a payroll record for an employee.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2 col-span-3">
                <Label>Employee *</Label>
                <Select 
                  value={payrollForm.employee_id} 
                  onValueChange={(value) => {
                    setPayrollForm({ ...payrollForm, employee_id: value });
                    calculatePayroll(value);
                  }}
                  disabled={!!editingPayroll}
                >
                  <SelectTrigger data-testid="payroll-employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.filter(e => e.is_active).map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name} - {emp.position} ({formatCurrency(emp.basic_salary)}/mo)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Pay Period Start *</Label>
                <Input
                  type="date"
                  value={payrollForm.pay_period_start}
                  onChange={(e) => setPayrollForm({ ...payrollForm, pay_period_start: e.target.value })}
                  data-testid="payroll-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Pay Period End *</Label>
                <Input
                  type="date"
                  value={payrollForm.pay_period_end}
                  onChange={(e) => setPayrollForm({ ...payrollForm, pay_period_end: e.target.value })}
                  data-testid="payroll-end-date"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium text-green-600 mb-3">Earnings</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Days Worked</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={payrollForm.days_worked}
                    onChange={(e) => setPayrollForm({ ...payrollForm, days_worked: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Basic Pay (PHP) *</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={payrollForm.basic_pay}
                    onChange={(e) => setPayrollForm({ ...payrollForm, basic_pay: e.target.value })}
                    data-testid="payroll-basic-pay"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Overtime Hours</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={payrollForm.overtime_hours}
                    onChange={(e) => setPayrollForm({ ...payrollForm, overtime_hours: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Overtime Pay</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={payrollForm.overtime_pay}
                    onChange={(e) => setPayrollForm({ ...payrollForm, overtime_pay: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Allowances</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={payrollForm.allowances}
                    onChange={(e) => setPayrollForm({ ...payrollForm, allowances: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bonuses</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={payrollForm.bonuses}
                    onChange={(e) => setPayrollForm({ ...payrollForm, bonuses: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium text-red-600 mb-3">Deductions</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>SSS Contribution</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={payrollForm.sss_contribution}
                    onChange={(e) => setPayrollForm({ ...payrollForm, sss_contribution: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>PhilHealth</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={payrollForm.philhealth_contribution}
                    onChange={(e) => setPayrollForm({ ...payrollForm, philhealth_contribution: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pag-IBIG</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={payrollForm.pagibig_contribution}
                    onChange={(e) => setPayrollForm({ ...payrollForm, pagibig_contribution: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tax Withheld</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={payrollForm.tax_withheld}
                    onChange={(e) => setPayrollForm({ ...payrollForm, tax_withheld: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Other Deductions</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={payrollForm.other_deductions}
                    onChange={(e) => setPayrollForm({ ...payrollForm, other_deductions: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Deduction Notes</Label>
                  <Input
                    placeholder="e.g., Cash advance"
                    value={payrollForm.deduction_notes}
                    onChange={(e) => setPayrollForm({ ...payrollForm, deduction_notes: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Additional notes"
                value={payrollForm.notes}
                onChange={(e) => setPayrollForm({ ...payrollForm, notes: e.target.value })}
              />
            </div>

            {/* Payroll Summary */}
            <div className="bg-secondary/50 rounded-lg p-4 mt-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-sm text-muted-foreground">Gross Pay</p>
                  <p className="text-lg font-bold text-blue-600">
                    {formatCurrency(
                      (parseFloat(payrollForm.basic_pay) || 0) +
                      (parseFloat(payrollForm.overtime_pay) || 0) +
                      (parseFloat(payrollForm.allowances) || 0) +
                      (parseFloat(payrollForm.bonuses) || 0)
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Deductions</p>
                  <p className="text-lg font-bold text-red-600">
                    {formatCurrency(
                      (parseFloat(payrollForm.sss_contribution) || 0) +
                      (parseFloat(payrollForm.philhealth_contribution) || 0) +
                      (parseFloat(payrollForm.pagibig_contribution) || 0) +
                      (parseFloat(payrollForm.tax_withheld) || 0) +
                      (parseFloat(payrollForm.other_deductions) || 0)
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Net Pay</p>
                  <p className="text-lg font-bold text-green-600">
                    {formatCurrency(
                      ((parseFloat(payrollForm.basic_pay) || 0) +
                      (parseFloat(payrollForm.overtime_pay) || 0) +
                      (parseFloat(payrollForm.allowances) || 0) +
                      (parseFloat(payrollForm.bonuses) || 0)) -
                      ((parseFloat(payrollForm.sss_contribution) || 0) +
                      (parseFloat(payrollForm.philhealth_contribution) || 0) +
                      (parseFloat(payrollForm.pagibig_contribution) || 0) +
                      (parseFloat(payrollForm.tax_withheld) || 0) +
                      (parseFloat(payrollForm.other_deductions) || 0))
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayrollModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddPayroll} data-testid="save-payroll-btn">
              {editingPayroll ? 'Update' : 'Create'} Payroll
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Employee Confirmation */}
      <Dialog open={!!deleteEmployeeConfirm} onOpenChange={() => setDeleteEmployeeConfirm(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Remove Employee
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this employee? They will be deactivated if they have payroll history.
            </DialogDescription>
          </DialogHeader>
          {deleteEmployeeConfirm && (
            <div className="py-4 px-4 bg-secondary/50 rounded-lg">
              <p className="font-medium">{deleteEmployeeConfirm.name}</p>
              <p className="text-sm text-muted-foreground">{deleteEmployeeConfirm.position} - {deleteEmployeeConfirm.department}</p>
              <p className="text-sm text-blue-600 mt-1">Salary: {formatCurrency(deleteEmployeeConfirm.basic_salary)}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteEmployeeConfirm(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDeleteEmployee(deleteEmployeeConfirm?.id)}
              data-testid="confirm-delete-employee"
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Payroll Confirmation */}
      <Dialog open={!!deletePayrollConfirm} onOpenChange={() => setDeletePayrollConfirm(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Delete Payroll Record
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this payroll record? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deletePayrollConfirm && (
            <div className="py-4 px-4 bg-secondary/50 rounded-lg">
              <p className="font-medium">{deletePayrollConfirm.employee_name}</p>
              <p className="text-sm text-muted-foreground">
                {formatDate(deletePayrollConfirm.pay_period_start)} - {formatDate(deletePayrollConfirm.pay_period_end)}
              </p>
              <p className="text-lg font-bold text-green-600 mt-2">Net Pay: {formatCurrency(deletePayrollConfirm.net_pay)}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletePayrollConfirm(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDeletePayroll(deletePayrollConfirm?.id)}
              data-testid="confirm-delete-payroll"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
